import React from 'react';
import { Palette, Type, Layout, Image as ImageIcon } from 'lucide-react';
import { FONT_OPTIONS, LAYOUT_TEMPLATES } from '../../constants/outreach';
import SegmentedControl from '../SegmentedControl';
import type { BrandingValues } from './brandingTypes';

interface Props {
  value: BrandingValues;
  onChange: (patch: Partial<BrandingValues>) => void;
  /** Reports have no layout templates to apply, so the section is hidden. */
  showLayoutTemplate?: boolean;
  /** A surface that is following the shared brand shows its values, uneditable. */
  readOnly?: boolean;
}

const COLOR_FIELDS: Array<{ key: keyof BrandingValues; label: string }> = [
  { key: 'primary_color', label: 'Primary Color' },
  { key: 'secondary_color', label: 'Secondary Color' },
  { key: 'background_color', label: 'Background' },
  { key: 'text_color', label: 'Text Color' },
];

/**
 * The branding field groups for one scope.
 *
 * Split out of the old OutreachBrandingTab so the same controls can render for
 * the shared brand and for either surface override without the tab knowing
 * anything about individual fields.
 */
const BrandingEditor: React.FC<Props> = ({
  value,
  onChange,
  showLayoutTemplate = true,
  readOnly = false,
}) => (
  <div className="space-y-6" aria-disabled={readOnly || undefined}>
    {/* Logo */}
    <div className="border border-border-light dark:border-border-dark rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <ImageIcon className="w-5 h-5 text-primary-500" />
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary">Logo Settings</h4>
      </div>

      <div className="space-y-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={value.show_organization_logo}
            disabled={readOnly}
            onChange={(e) => onChange({ show_organization_logo: e.target.checked })}
            className="rounded"
          />
          <span className="text-text-primary dark:text-text-dark-primary">
            Show organization logo
          </span>
        </label>

        <div>
          <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
            Logo Position
          </label>
          {readOnly ? (
            <p className="text-sm text-text-primary dark:text-text-dark-primary capitalize">
              {value.logo_position}
            </p>
          ) : (
            <SegmentedControl
              ariaLabel="Logo position"
              value={value.logo_position}
              onChange={(logo_position) => onChange({ logo_position })}
              options={[
                { id: 'left', label: 'Left' },
                { id: 'center', label: 'Center' },
                { id: 'right', label: 'Right' },
              ]}
            />
          )}
        </div>
      </div>
    </div>

    {/* Colors */}
    <div className="border border-border-light dark:border-border-dark rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="w-5 h-5 text-primary-500" />
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary">Colors</h4>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {COLOR_FIELDS.map(({ key, label }) => (
          <div key={key}>
            <label
              className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
              htmlFor={`branding-${key}`}
            >
              {label}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                aria-label={`${label} swatch`}
                value={value[key] as string}
                disabled={readOnly}
                onChange={(e) => onChange({ [key]: e.target.value } as Partial<BrandingValues>)}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <input
                id={`branding-${key}`}
                type="text"
                value={value[key] as string}
                disabled={readOnly}
                onChange={(e) => onChange({ [key]: e.target.value } as Partial<BrandingValues>)}
                className="input flex-1 font-mono text-sm"
              />
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Typography */}
    <div className="border border-border-light dark:border-border-dark rounded-lg p-4">
      <div className="flex items-center gap-2 mb-4">
        <Type className="w-5 h-5 text-primary-500" />
        <h4 className="font-medium text-text-primary dark:text-text-dark-primary">Typography</h4>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
            htmlFor="branding-heading-font"
          >
            Heading Font
          </label>
          <select
            id="branding-heading-font"
            value={value.heading_font}
            disabled={readOnly}
            onChange={(e) => onChange({ heading_font: e.target.value })}
            className="input"
          >
            {FONT_OPTIONS.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
            htmlFor="branding-body-font"
          >
            Body Font
          </label>
          <select
            id="branding-body-font"
            value={value.body_font}
            disabled={readOnly}
            onChange={(e) => onChange({ body_font: e.target.value })}
            className="input"
          >
            {FONT_OPTIONS.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>

    {/* Layout template — shared and outreach only */}
    {showLayoutTemplate && (
      <div className="border border-border-light dark:border-border-dark rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Layout className="w-5 h-5 text-primary-500" />
          <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
            Layout Template
          </h4>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {LAYOUT_TEMPLATES.map((template) => (
            <button
              key={template.value}
              type="button"
              disabled={readOnly}
              onClick={() =>
                onChange({ layout_template: template.value as BrandingValues['layout_template'] })
              }
              className={`p-3 rounded-lg border text-left transition-colors ${
                value.layout_template === template.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-border-light dark:border-border-dark hover:border-primary-300'
              }`}
            >
              <div className="font-medium text-text-primary dark:text-text-dark-primary text-sm">
                {template.label}
              </div>
              <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                {template.description}
              </div>
            </button>
          ))}
        </div>
      </div>
    )}

    {/* Custom HTML */}
    <div className="border border-border-light dark:border-border-dark rounded-lg p-4">
      <h4 className="font-medium text-text-primary dark:text-text-dark-primary mb-4">
        Custom Content (Optional)
      </h4>

      <div className="space-y-4">
        <div>
          <label
            className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
            htmlFor="branding-header-html"
          >
            Custom Header HTML
          </label>
          <textarea
            id="branding-header-html"
            value={value.custom_header_html || ''}
            disabled={readOnly}
            onChange={(e) => onChange({ custom_header_html: e.target.value || null })}
            className="input font-mono text-sm"
            rows={3}
            placeholder="<div>Custom header content...</div>"
          />
        </div>

        <div>
          <label
            className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2"
            htmlFor="branding-footer-html"
          >
            Custom Footer HTML
          </label>
          <textarea
            id="branding-footer-html"
            value={value.custom_footer_html || ''}
            disabled={readOnly}
            onChange={(e) => onChange({ custom_footer_html: e.target.value || null })}
            className="input font-mono text-sm"
            rows={3}
            placeholder="<div>Custom footer content...</div>"
          />
        </div>
      </div>
    </div>
  </div>
);

export default BrandingEditor;
